import {I18n} from '@aws-amplify/core';
import {DataTableColumn} from 'mantine-datatable';
import {NavLink} from 'react-router-dom';

import {Price} from '../types';
import {StatusBadge} from '../profile/StatusBadge';

// FNP-918: was `tables/columns.tsx` — a bare `getColumns()` array where EVERY
// column was `render`-only, with no `accessor` on any of them.
//
// That is the distinction that made this module unsortable. `render` controls
// how a cell LOOKS; `accessor` is what sorting sorts BY. A render-only config
// looks complete in review and makes sorting structurally impossible — you
// cannot add `sortable: true` to a column that has nothing to sort on. Moved
// into a hook to match the platform shape (checklist §3z).
//
// ⚠️ THE CREATED COLUMN IS GONE, deliberately.
//
// `listPrices` does not SELECT `created` — open services/queries.ts and it is
// not in the field list, on either the list or the get query. The old column
// therefore rendered `formatTimestamp(undefined)` for every row, which the
// shared formatter correctly turns into an em-dash. So the column was a
// full-width strip of placeholders that looked like missing data and was in
// fact a field nobody had ever fetched.
//
// Adding it back means adding `created` to the GraphQL selection set — a change
// to what this module asks the API for, which is a decision about the query,
// not a column config. Removing the dead column is the honest half; the field
// can be added when someone wants it.
//
// ⚠️ `unitAmount` arrives from the API as a STRING of CENTS and is normalised
// to a NUMBER of currency units in usePrices. Sorting therefore compares
// numerically. Had it stayed a string, "100" would sort before "9".
export const usePricesTableColumns = () => {

    const columns: DataTableColumn<Price>[] = [
        {
            accessor: 'id',
            sortable: true,
            title: I18n.get('Price'),
            width: '1fr',
            // A real link to the detail route. The old column rendered a
            // <span> with an inline blue colour and a cursor style faking a
            // link — not keyboard-reachable, no focus ring, and it opened an
            // edit drawer rather than navigating (§2 no hardcoded colours,
            // §13 accessibility).
            render: (item: Price) => (
                <NavLink to={`/prices/${encodeURIComponent(item.id)}`}>
                    {item.id}
                </NavLink>
            ),
        },
        {
            accessor: 'nickname',
            sortable: true,
            title: I18n.get('Nickname'),
            width: '1fr',
            // Nullable — a price need not be named. Em-dash placeholder,
            // never blank or `undefined` (§13).
            render: (item: Price) => item.nickname || '—',
        },
        {
            accessor: 'product',
            sortable: true,
            title: I18n.get('Product'),
            width: '1fr',
            render: (item: Price) => (
                item.product
                    ? (
                        <NavLink to={`/products/${encodeURIComponent(item.product)}`}>
                            {item.product}
                        </NavLink>
                    )
                    : '—'
            ),
        },
        {
            // Numeric after normalisation in usePrices. See the note above.
            accessor: 'unitAmount',
            sortable: true,
            title: I18n.get('Unit Amount'),
            width: '1fr',
            render: (item: Price) => (
                item.unitAmount === null || typeof item.unitAmount === 'undefined'
                    ? '—'
                    : `${item.unitAmount} ${(item.currency ?? '').toUpperCase()}`
            ),
        },
        {
            accessor: 'active',
            sortable: true,
            title: I18n.get('Active'),
            width: '1fr',
            // Boolean state renders as a badge, not a raw string (§13).
            render: (item: Price) => <StatusBadge active={item.active}/>,
        },
    ];

    return {columns};
};
